import { Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';

const CONDITIONS = [
  { id: '', name: 'Any Condition' },
  { id: 'excellent', name: 'Excellent' },
  { id: 'good', name: 'Good' },
  { id: 'fair', name: 'Fair' },
  { id: 'salvage', name: 'Salvage' },
];

/**
 * Modal that lets a user define a new "notify me when matching items appear" alert.
 * Extracted from AlertsPage.
 */
export default function CreateAlertModal({
  open,
  onOpenChange,
  newAlert,
  setNewAlert,
  categories,
  creating,
  onCreate,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border max-w-md">
        <DialogHeader>
          <DialogTitle className="font-playfair text-xl">Create Material Alert</DialogTitle>
          <DialogDescription>
            Get notified when materials matching your criteria are listed.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <label className="text-sm text-muted-foreground mb-2 block">Category *</label>
            <Select
              value={newAlert.category}
              onValueChange={(v) => setNewAlert({ ...newAlert, category: v })}
            >
              <SelectTrigger className="w-full bg-secondary/30 border-border/40" data-testid="alert-category-select">
                <SelectValue placeholder="Select category" />
              </SelectTrigger>
              <SelectContent>
                {categories.map((cat) => (
                  <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="text-sm text-muted-foreground mb-2 block">Keywords (optional)</label>
            <input
              type="text"
              value={newAlert.keywords}
              onChange={(e) => setNewAlert({ ...newAlert, keywords: e.target.value })}
              className="w-full h-10 px-4 bg-secondary/30 border border-border/40 rounded-md text-foreground placeholder:text-muted-foreground/50"
              placeholder="e.g., Victorian, brass, oak"
              data-testid="alert-keywords-input"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-muted-foreground mb-2 block">Condition</label>
              <Select
                value={newAlert.condition}
                onValueChange={(v) => setNewAlert({ ...newAlert, condition: v })}
              >
                <SelectTrigger className="w-full bg-secondary/30 border-border/40">
                  <SelectValue placeholder="Any" />
                </SelectTrigger>
                <SelectContent>
                  {CONDITIONS.map((cond) => (
                    <SelectItem key={cond.id || 'any'} value={cond.id}>{cond.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm text-muted-foreground mb-2 block">Max Price</label>
              <input
                type="number"
                value={newAlert.max_price}
                onChange={(e) => setNewAlert({ ...newAlert, max_price: e.target.value })}
                className="w-full h-10 px-4 bg-secondary/30 border border-border/40 rounded-md text-foreground"
                placeholder="No limit"
                data-testid="alert-price-input"
              />
            </div>
          </div>

          <div>
            <label className="text-sm text-muted-foreground mb-2 block">Location (optional)</label>
            <input
              type="text"
              value={newAlert.location}
              onChange={(e) => setNewAlert({ ...newAlert, location: e.target.value })}
              className="w-full h-10 px-4 bg-secondary/30 border border-border/40 rounded-md text-foreground placeholder:text-muted-foreground/50"
              placeholder="e.g., London, UK"
              data-testid="alert-location-input"
            />
          </div>

          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={newAlert.notify_email}
              onChange={(e) => setNewAlert({ ...newAlert, notify_email: e.target.checked })}
              className="w-5 h-5 rounded border-border/40 bg-secondary/30 text-accent"
            />
            <span className="text-sm text-foreground">Email me when new matches are found</span>
          </label>

          <div className="flex gap-3 pt-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} className="flex-1">
              Cancel
            </Button>
            <Button
              onClick={onCreate}
              disabled={!newAlert.category || creating}
              className="flex-1 btn-gold"
              data-testid="submit-alert-btn"
            >
              {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create Alert'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
